// =============================================================================
// Inventory Management — inbound/outbound shipments, and the module serial search.
//
// The serial search is what a warranty claim rests on: it answers "where did this exact module end up"
// in one query, which the spreadsheet could only answer by scrolling 70,000 rows.
// =============================================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { strings } from '/imports/strings';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { useMethod } from '/imports/hooks';
import {
  inventoryListLots, inventoryListShipments, inventoryListWarehouses, inventorySearchSerials,
  inventoryShipmentDirectionLabel, inventoryShipmentStatusLabel, inventoryShipmentStatuses,
} from '/imports/api/inventory';
import {
  EmptyRow, InventoryPageStyled, PalletStatusChip, count, day, orDash, useInventoryAccess,
} from '/imports/ui/InventoryShared';

/** The delivery log: inbound loads from the vendor, and the outbound legs deliveries write. */
export const InventoryShipmentsPage = () => {
  const { canAccess } = useInventoryAccess();
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('');
  const [status, setStatus] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [warehouses] = useMethod(inventoryListWarehouses, undefined, []);
  const [lots] = useMethod(inventoryListLots, undefined, []);
  const [rows, loading] = useMethod(
    inventoryListShipments,
    { direction: direction || undefined, status: (status || undefined) as any, poNumber: poNumber || undefined,
      warehouseId: warehouseId || undefined, search: search || undefined },
    [direction, status, poNumber, warehouseId, search],
  );
  if (!canAccess) return <NotPermittedPage />;

  const poOptions = [...new Set((lots || []).map((l) => l.poNumber).filter(Boolean) as string[])];

  return (
    <InventoryPageStyled className="page">
      <PageHeading left={<h1>{strings.InventoryShipments()}</h1>} />
      <PageBody>
        <div className="inv-bar">
          <Input className="search" value={search} placeholder={strings.InventorySearchShipmentsPlaceholder()} onChange={(v) => setSearch(v || '')} />
          <Select
            value={direction}
            options={[
              { value: '', label: strings.InventoryAllDirections() },
              { value: 'inbound', label: strings.InventoryDirectionInbound() },
              { value: 'outbound', label: strings.InventoryDirectionOutbound() },
              { value: 'transfer', label: strings.InventoryDirectionTransfer() },
            ]}
            onChange={setDirection}
          />
          <Select
            value={status}
            options={[{ value: '', label: strings.InventoryAllStatuses() }, ...inventoryShipmentStatuses.map((s) => ({ value: s, label: inventoryShipmentStatusLabel(s) }))]}
            onChange={setStatus}
          />
          <Select
            value={poNumber}
            options={[{ value: '', label: strings.InventoryAllLots() }, ...poOptions.map((p) => ({ value: p, label: `${strings.InventoryPo()} ${p}` }))]}
            onChange={setPoNumber}
          />
          <Select
            value={warehouseId}
            options={[{ value: '', label: strings.InventoryAllWarehouses() }, ...(warehouses || []).map((w) => ({ value: w._id, label: `${w.code} — ${w.city}` }))]}
            onChange={setWarehouseId}
          />
          <span className="spacer" />
          <span className="meta">{count(rows?.length)}</span>
        </div>

        <div className="scroll-x">
          <table className="inv">
            <thead>
              <tr>
                <th>{strings.InventoryShipmentId()}</th>
                <th>{strings.Direction()}</th>
                <th>{strings.InventoryPo()}</th>
                <th>{strings.InventoryOrigin()}</th>
                <th>{strings.InventoryDestination()}</th>
                <th className="num">{strings.InventoryPallet()}</th>
                <th className="num">{strings.InventoryTrucks()}</th>
                <th className="num">{strings.InventoryTotalUnits()}</th>
                <th>{strings.InventoryOutboundDate()}</th>
                <th>{strings.InventoryExpectedDelivery()}</th>
                <th>{strings.InventoryInboundDate()}</th>
                <th>{strings.Status()}</th>
                <th>{strings.InventoryBol()}</th>
              </tr>
            </thead>
            <tbody>
              {loading && !rows && <tr><td colSpan={13}><LoadingFlex /></td></tr>}
              {(rows || []).map((s) => (
                <tr key={s._id}>
                  <td>{s.shipmentId}</td>
                  <td>{inventoryShipmentDirectionLabel(s.direction)}</td>
                  <td>{orDash(s.poNumber)}</td>
                  <td>{orDash(s.origin)}</td>
                  <td>
                    {s.destinationSiteId
                      ? <Link to={`/inventory/sites/${s.destinationSiteId}`}>{s.destinationSiteId}</Link>
                      : s.destinationWarehouseId
                        ? <Link to={`/inventory/warehouses/${s.destinationWarehouseId}`}>{strings.InventoryWarehouse()}</Link>
                        : orDash(undefined)}
                  </td>
                  <td className="num">{count(s.pallets)}</td>
                  <td className="num">{count(s.trucks)}</td>
                  <td className="num">{count(s.units)}</td>
                  <td>{day(s.outboundDate)}</td>
                  <td>{day(s.expectedDelivery)}</td>
                  <td>{day(s.inboundDate)}</td>
                  <td>{inventoryShipmentStatusLabel(s.status)}</td>
                  <td>{orDash(s.bol)}</td>
                </tr>
              ))}
              {!loading && !rows?.length && <EmptyRow colSpan={13} label={strings.InventoryNoShipments()} />}
            </tbody>
          </table>
        </div>
      </PageBody>
    </InventoryPageStyled>
  );
};

/** Find a module by serial, pallet, container, bill of lading or batch, and see where it ended up. */
export const InventorySerialsPage = () => {
  const { canAccess } = useInventoryAccess();
  const [search, setSearch] = useState('');
  const [rows, loading] = useMethod(inventorySearchSerials, { search, limit: 200 }, [search], () => !!search.trim());
  if (!canAccess) return <NotPermittedPage />;

  return (
    <InventoryPageStyled className="page">
      <PageHeading left={<h1>{strings.InventorySerials()}</h1>} />
      <PageBody>
        <div className="inv-bar">
          <Input className="search" value={search} placeholder={strings.InventorySearchSerialsPlaceholder()} onChange={(v) => setSearch(v || '')} />
          <span className="spacer" />
          <span className="meta">{count(rows?.length)}</span>
        </div>

        {!search.trim() && <div className="empty">{strings.InventorySearchSerialsPlaceholder()}</div>}
        {!!search.trim() && (
          <div className="scroll-x">
            <table className="inv">
              <thead>
                <tr>
                  <th>{strings.InventorySerialNumber()}</th>
                  <th>{strings.InventoryPalletNumber()}</th>
                  <th className="num">{strings.InventoryPosition()}</th>
                  <th>{strings.InventoryModuleModel()}</th>
                  <th className="num">{strings.InventoryNameplate()}</th>
                  <th className="num">{strings.InventoryFlashPmax()}</th>
                  <th className="num">{strings.InventoryVoc()}</th>
                  <th className="num">{strings.InventoryIsc()}</th>
                  <th className="num">{strings.InventoryFillFactor()}</th>
                  <th>{strings.InventoryContainer()}</th>
                  <th>{strings.Batch()}</th>
                  <th>{strings.InventoryPlant()}</th>
                  <th>{strings.Status()}</th>
                  <th>{strings.InventorySiteId()}</th>
                </tr>
              </thead>
              <tbody>
                {loading && !rows && <tr><td colSpan={14}><LoadingFlex /></td></tr>}
                {(rows || []).map((m) => (
                  <tr key={m._id}>
                    <td>{m.serial}</td>
                    <td><Link to={`/inventory/pallet-inventory/${m.palletId}`}>{m.palletNumber}</Link></td>
                    <td className="num">{count(m.position)}</td>
                    <td>{orDash(m.moduleModel)}</td>
                    <td className="num">{count(m.nameplateW)}</td>
                    <td className="num">{m.flash?.pmax ?? orDash(undefined)}</td>
                    <td className="num">{m.flash?.voc ?? orDash(undefined)}</td>
                    <td className="num">{m.flash?.isc ?? orDash(undefined)}</td>
                    <td className="num">{m.flash?.ff ?? orDash(undefined)}</td>
                    <td>{orDash(m.container)}</td>
                    <td>{orDash(m.batch)}</td>
                    <td>{orDash(m.plant)}</td>
                    <td>{m.palletStatus ? <PalletStatusChip status={m.palletStatus} /> : orDash(undefined)}</td>
                    <td>{m.siteId ? <Link to={`/inventory/sites/${m.siteId}`}>{m.siteId}</Link> : orDash(undefined)}</td>
                  </tr>
                ))}
                {!loading && !rows?.length && <EmptyRow colSpan={14} label={strings.InventoryNoSerials()} />}
              </tbody>
            </table>
          </div>
        )}
      </PageBody>
    </InventoryPageStyled>
  );
};
